// src/components/ReceiptScanner.js
import React, { useState, useRef } from 'react';
import { Camera, Upload, Trash, Loader } from 'lucide-react'; // アイコンのインポート
import { processReceiptImage } from '../services/ReceiptScanService'; // 新しいサービスをインポート

const ReceiptScanner = ({ onScanComplete }) => {
    const [image, setImage] = useState(null); // 選択された画像 (Base64 URL)
    const [extractedData, setExtractedData] = useState(null); // 抽出されたデータ
    const [isLoading, setIsLoading] = useState(false); // 処理中の状態
    const [error, setError] = useState(null); // エラーメッセージ
    const fileInputRef = useRef(null); // ファイル入力参照用

    // ファイル選択ハンドラ
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImage(reader.result); // Base64エンコードされた画像をセット
                setExtractedData(null); // 新しい画像が選択されたら以前の抽出データをリセット
                setError(null); // エラーもリセット
            };
            reader.readAsDataURL(file); // 画像をBase64として読み込む
        } else {
            setImage(null);
            setError('画像ファイルを選択してください。');
        }
    };

    // カメラボタンクリックハンドラ (ファイル入力要素をトリガー)
    const handleCameraButtonClick = () => {
        // モバイルデバイスで`capture="environment"`を使用すると、直接背面カメラが開く可能性があります。
        // デスクトップでは通常のファイル選択ダイアログになります。
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.capture = 'environment'; // モバイルの背面カメラを優先
        input.onchange = handleFileChange;
        input.click();
    };

    // OCR処理の実行
    const handleProcessReceipt = async () => {
        if (!image) {
            setError('レシート画像をアップロードまたは撮影してください。');
            return;
        }

        setIsLoading(true);
        setError(null);
        setExtractedData(null);

        try {
            // ReceiptScanServiceの関数を呼び出す
            const data = await processReceiptImage(image);
            setExtractedData(data); // 抽出されたデータをセット
            onScanComplete(data); // 親コンポーネント (App.js) にデータを渡す

        } catch (err) {
            console.error('レシート処理エラー:', err);
            setError(`レシート処理中にエラーが発生しました: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // 画像クリアハンドラ
    const handleClearImage = () => {
        setImage(null);
        setExtractedData(null);
        setError(null);
    };

    return (
        <div className="p-6 bg-white rounded-lg shadow-xl mb-8 border border-blue-200">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">レシート読み取り</h2>

            {/* 画像アップロード/撮影エリア */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center mb-6 bg-gray-50 flex flex-col items-center justify-center min-h-[200px]">
                {image ? (
                    <div className="relative w-full h-full max-h-[300px] overflow-hidden rounded-md">
                        <img src={image} alt="Uploaded Receipt" className="max-w-full h-auto mx-auto object-contain rounded-md" />
                        <button
                            onClick={handleClearImage}
                            className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-md transition-colors duration-200"
                            aria-label="画像を削除"
                        >
                            <Trash size={18} />
                        </button>
                    </div>
                ) : (
                    <>
                        <p className="text-gray-600 mb-4">レシートの写真を撮るか、画像を選択してください</p>
                        <div className="flex space-x-4">
                            <button
                                onClick={handleCameraButtonClick}
                                className="bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg shadow-md flex items-center space-x-2 transition duration-200 ease-in-out transform hover:scale-105"
                            >
                                <Camera size={20} />
                                <span>カメラで撮影</span>
                            </button>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                ref={fileInputRef}
                                className="hidden"
                            />
                            <button
                                onClick={() => fileInputRef.current.click()}
                                className="bg-purple-500 hover:bg-purple-600 text-white font-semibold py-3 px-6 rounded-lg shadow-md flex items-center space-x-2 transition duration-200 ease-in-out transform hover:scale-105"
                            >
                                <Upload size={20} />
                                <span>画像を選択</span>
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* 処理ボタンとローディング/メッセージ */}
            {image && (
                <div className="mb-6">
                    <button
                        onClick={handleProcessReceipt}
                        disabled={isLoading}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-md shadow-md transition duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                        {isLoading && <Loader size={20} className="animate-spin" />}
                        <span>{isLoading ? '処理中...' : 'レシートを読み取る'}</span>
                    </button>
                </div>
            )}

            {/* エラーメッセージ */}
            {error && (
                <p className="text-red-600 text-center text-sm mt-4">{error}</p>
            )}

            {/* 抽出されたデータの表示 (デバッグ/確認用) */}
            {extractedData && (
                <div className="bg-blue-50 p-4 rounded-md border border-blue-200 mt-6">
                    <h3 className="text-lg font-semibold text-blue-800 mb-3">抽出された情報:</h3>
                    <ul className="text-gray-700 text-sm space-y-1">
                        {Object.entries(extractedData).map(([key, value]) => (
                            <li key={key}>
                                <strong className="text-blue-700">{key}:</strong> {value}
                            </li>
                        ))}
                    </ul>
                    {/* ここで、抽出されたデータを取引入力フォームに自動入力するボタンなどを追加することも可能 */}
                </div>
            )}
        </div>
    );
};

export default ReceiptScanner;
